import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { AppLayout } from '@/components/shared/AppLayout';
import { ProtectedRoute } from '@/components/shared/ProtectedRoute';
import { RootRedirect } from '@/components/shared/RootRedirect';
import { TenantRequiredRoute } from '@/components/shared/TenantRequiredRoute';
import { TenantsRouteGuard } from '@/components/shared/TenantsRouteGuard';
import { AuthProvider } from '@/contexts/AuthContext';
import { TenantSwitcherProvider } from '@/contexts/TenantSwitcherContext';
import { AcceptInvitationPage } from '@/pages/auth/accept-invitation-page';
import { ForgotPasswordPage } from '@/pages/auth/forgot-password-page';
import { InvitePage } from '@/pages/auth/invite-page';
import { LoginPage } from '@/pages/auth/login-page';
import { ResetPasswordPage } from '@/pages/auth/reset-password-page';
import { BranchesPage } from '@/pages/branches/branches-page';
import { EditBranchPage } from '@/pages/branches/edit-branch-page';
import { CategoriesPage } from '@/pages/categories/categories-page';
import { EditCategoryPage } from '@/pages/categories/edit-category-page';
import { DashboardPage } from '@/pages/dashboard/dashboard-page';
import { DetectionsPage } from '@/pages/detections/detections-page';
import { DetectionDetailPage } from '@/pages/detections/detection-detail-page';
import { UsersPage } from '@/pages/users/users-page';
import { UserDetailsPage } from '@/pages/users/user-details-page';
import { ItemsPage } from '@/pages/items/items-page';
import { ItemDetailsPage } from '@/pages/items/item-details-page';
import { ItemPrintQrPage } from '@/pages/items/item-print-qr-page';
import { ItemPrintQrBatchPage } from '@/pages/items/item-print-qr-batch-page';
import { EditItemPage } from '@/pages/items/edit-item-page';
import { ScannersPage } from '@/pages/scanners/scanners-page';
import { ScannerDetailsPage } from '@/pages/scanners/scanner-details-page';
import { EditScannerPage } from '@/pages/scanners/edit-scanner-page';
import { DeviceModelsPage } from '@/pages/device-models/device-models-page';
import { EditDeviceModelPage } from '@/pages/device-models/edit-device-model-page';
import { SettingsPage } from '@/pages/settings/settings-page';
import { ShelvesPage } from '@/pages/shelves/shelves-page';
import { EditShelfPage } from '@/pages/shelves/edit-shelf-page';
import { TenantsPage } from '@/pages/tenants/tenants-page';
import { EditTenantPage } from '@/pages/tenants/edit-tenant-page';

function App(): JSX.Element {
  return (
    <AuthProvider>
      <Toaster position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route
            path="/accept-invitation/:token"
            element={<AcceptInvitationPage />}
          />
          <Route
            path="/items/:id/print-qr"
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <TenantRequiredRoute>
                    <ItemPrintQrPage />
                  </TenantRequiredRoute>
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/items/print-qr-batch"
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <TenantRequiredRoute>
                    <ItemPrintQrBatchPage />
                  </TenantRequiredRoute>
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <AppLayout />
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          >
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={['root', 'admin']}>
                  <TenantRequiredRoute>
                    <DashboardPage />
                  </TenantRequiredRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/items"
              element={
                <TenantRequiredRoute>
                  <ItemsPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/items/:id"
              element={
                <TenantRequiredRoute>
                  <ItemDetailsPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/items/:id/edit"
              element={
                <TenantRequiredRoute>
                  <ProtectedRoute allowedRoles={['root', 'admin']}>
                    <EditItemPage />
                  </ProtectedRoute>
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/shelves"
              element={
                <TenantRequiredRoute>
                  <ShelvesPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/shelves/:id/edit"
              element={
                <TenantRequiredRoute>
                  <ProtectedRoute allowedRoles={['root', 'admin']}>
                    <EditShelfPage />
                  </ProtectedRoute>
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/branches"
              element={
                <ProtectedRoute allowedRoles={['root', 'admin']}>
                  <TenantRequiredRoute>
                    <BranchesPage />
                  </TenantRequiredRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/branches/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['root', 'admin']}>
                  <TenantRequiredRoute>
                    <EditBranchPage />
                  </TenantRequiredRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <TenantRequiredRoute>
                  <CategoriesPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/categories/:id/edit"
              element={
                <TenantRequiredRoute>
                  <ProtectedRoute allowedRoles={['root', 'admin']}>
                    <EditCategoryPage />
                  </ProtectedRoute>
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/scanners"
              element={
                <ProtectedRoute allowedRoles={['root', 'admin']}>
                  <ScannersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scanners/:id"
              element={
                <ProtectedRoute allowedRoles={['root', 'admin']}>
                  <ScannerDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scanners/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['root']}>
                  <EditScannerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/device-models"
              element={
                <ProtectedRoute allowedRoles={['root']}>
                  <DeviceModelsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/device-models/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['root']}>
                  <EditDeviceModelPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/detections/:id"
              element={
                <ProtectedRoute allowedRoles={['root', 'admin']}>
                  <TenantRequiredRoute>
                    <DetectionDetailPage />
                  </TenantRequiredRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/detections"
              element={
                <ProtectedRoute allowedRoles={['root', 'admin']}>
                  <TenantRequiredRoute>
                    <DetectionsPage />
                  </TenantRequiredRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <TenantRequiredRoute>
                  <SettingsPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={['root', 'admin']}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <UserDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:id"
              element={
                <ProtectedRoute allowedRoles={['root', 'admin']}>
                  <UserDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenants"
              element={
                <ProtectedRoute allowedRoles={['root']}>
                  <TenantsRouteGuard>
                    <TenantsPage />
                  </TenantsRouteGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenants/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['root']}>
                  <EditTenantPage />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <RootRedirect />
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <RootRedirect />
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
